import { useMemo } from "react";
import type { Creative, Hypothesis } from "@lovmia/shared";
import { useNavigate } from "../router";
import { PageHeader } from "../components/Ui";
import { useAppState } from "../state";

/**
 * Производство по дням. Ролик засчитывается в день, когда у крео ПОЯВИЛОСЬ
 * видео (file.createdAt), а не когда завели карточку: карточка без видео —
 * ещё не сделанный ролик, и темп фабрики меряется готовыми артефактами.
 */

const WINDOW_DAYS = 30;
/** Пустые дни показываем только в свежей недели — старые нули лишь растягивают список. */
const ALWAYS_SHOW_DAYS = 7;

/** Ключ дня в локальном времени: команда живёт по своему календарю, не по UTC. */
const keyOf = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const keyOfIso = (iso: string) => keyOf(new Date(iso));

const shiftDay = (base: Date, offset: number) => {
  const date = new Date(base);
  date.setDate(date.getDate() - offset);
  return date;
};

const pluralVideos = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "ролик";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "ролика";
  return "роликов";
};

const dayFormat = new Intl.DateTimeFormat("ru-RU", { weekday: "short", day: "numeric", month: "short" });

interface DayRow {
  key: string;
  date: Date;
  offset: number;
  /** Крео, у которых видео появилось в этот день. */
  videos: { creative: Creative; family: Hypothesis }[];
  /** Заведённых карточек крео за день (видео могло приехать позже). */
  cards: number;
  families: Hypothesis[];
}

export function StatsPage() {
  const { data } = useAppState();
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const today = new Date();
    const byDay = new Map<string, DayRow>();
    const ordered: DayRow[] = [];
    for (let offset = 0; offset < WINDOW_DAYS; offset += 1) {
      const date = shiftDay(today, offset);
      const row: DayRow = { key: keyOf(date), date, offset, videos: [], cards: 0, families: [] };
      byDay.set(row.key, row);
      ordered.push(row);
    }
    for (const family of data.hypotheses) {
      byDay.get(keyOfIso(family.createdAt))?.families.push(family);
      for (const creative of family.creatives) {
        const cardRow = byDay.get(keyOfIso(creative.createdAt));
        if (cardRow) cardRow.cards += 1;
        if (creative.file) byDay.get(keyOfIso(creative.file.createdAt))?.videos.push({ creative, family });
      }
    }
    for (const row of ordered) row.videos.sort((a, b) => b.creative.file!.createdAt.localeCompare(a.creative.file!.createdAt));
    return ordered;
  }, [data.hypotheses]);

  const countVideos = (days: number) => rows.slice(0, days).reduce((sum, row) => sum + row.videos.length, 0);
  const todayCount = rows[0]?.videos.length ?? 0;
  const yesterdayCount = rows[1]?.videos.length ?? 0;
  const weekCount = countVideos(7);
  const monthCount = countVideos(30);
  const maxPerDay = Math.max(1, ...rows.map((row) => row.videos.length));

  const visible = rows.filter((row) => row.offset < ALWAYS_SHOW_DAYS || row.videos.length > 0 || row.cards > 0 || row.families.length > 0);

  return <div className="page">
    <PageHeader eyebrow="production" title="Статистика" description="Сколько роликов делается по дням. Ролик засчитывается в день, когда у крео появилось видео; заведённые карточки и новые гипотезы — отдельной строкой." />
    <div className="stats-summary">
      <div className="stats-tile"><span>Сегодня</span><strong>{todayCount}</strong><em>{pluralVideos(todayCount)}</em></div>
      <div className="stats-tile"><span>Вчера</span><strong>{yesterdayCount}</strong><em>{pluralVideos(yesterdayCount)}</em></div>
      <div className="stats-tile"><span>За 7 дней</span><strong>{weekCount}</strong><em>≈ {(weekCount / 7).toFixed(1)} в день</em></div>
      <div className="stats-tile"><span>За 30 дней</span><strong>{monthCount}</strong><em>≈ {(monthCount / 30).toFixed(1)} в день</em></div>
    </div>
    <div className="stats-days">
      {visible.map((row) => {
        const label = row.offset === 0 ? "Сегодня" : row.offset === 1 ? "Вчера" : dayFormat.format(row.date);
        const parts = [
          row.videos.length ? `${row.videos.length} ${pluralVideos(row.videos.length)}` : null,
          row.cards ? `карточек: ${row.cards}` : null,
          row.families.length ? `гипотез: ${row.families.length}` : null
        ].filter(Boolean);
        const empty = parts.length === 0;
        return <div key={row.key} className={`stats-day ${empty ? "stats-day--empty" : ""}`}>
          <div className="stats-day__head">
            <strong>{label}</strong>
            <i className="stats-day__bar"><b style={{ width: `${Math.round(row.videos.length / maxPerDay * 100)}%` }} /></i>
            <em>{empty ? "—" : parts.join(" · ")}</em>
          </div>
          {(row.videos.length > 0 || row.families.length > 0) && <div className="stats-day__chips">
            {row.videos.map(({ creative }) => <button key={creative.id} className="stats-chip" title={creative.variationTitle ?? creative.adName} onClick={() => navigate(`/creatives/${creative.id}`)}>
              <img src={`/api/files/${creative.file!.id}/poster`} alt="" loading="lazy" onError={(event) => event.currentTarget.remove()} />
              <span>{creative.adName}</span>
            </button>)}
            {row.families.map((family) => <button key={family.id} className="stats-chip stats-chip--family" title={family.statement} onClick={() => navigate(`/hypotheses/${family.id}`)}>
              <b>{family.id}</b><span>{family.title ?? family.statement}</span>
            </button>)}
          </div>}
        </div>;
      })}
    </div>
  </div>;
}
